import axios from 'axios'
import moment from 'moment-timezone'
import { ActionContext, Module } from 'vuex'
import { RootState } from '../..'
import { generateValidRacetimeList, getCompetitorStatus } from './helpers'
import { Feature, FleetDatapoint, RhumblinePoint, YachtTrackPoint } from './types'
import { Status } from '../../types'
import { relativeTime } from '@/helpers/racetime'

export type RaceState = {
  version: number;
  status: Status;
  raceId: number;
  raceTime: string;
  raceTimeMin: string;
  raceTimeMax: string;
  raceDateTime: null | Date;
  raceTimeOptions: Array<string>;
  fleetData: Array<FleetDatapoint>;
  fleetSource: {
    type: 'geojson';
    data: {
      type: 'FeatureCollection';
      features: Feature[];
    };
  };
  rhumbline: Array<RhumblinePoint>;
  tracks: Array<{ raceEntryId: number; track: Array<YachtTrackPoint>; }>;
}

type RaceOptions = {
  raceId: number;
  raceTime: string;
  raceTimeMin: string;
  raceTimeMax: string;
}

const TRACK_LENGTH = 5

function getInitialState (): RaceState {
  return {
    version: 0,
    status: Status.NotStarted,
    raceId: 0,
    raceTime: '',
    raceTimeMin: '',
    raceTimeMax: '',
    raceDateTime: null,
    raceTimeOptions: [],
    fleetData: [],
    fleetSource: {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [] as Feature[]
      }
    },
    rhumbline: [] as Array<RhumblinePoint>,
    tracks: []
  }
}

const raceModule: Module<RaceState, RootState> = {
  namespaced: true,
  state: (): RaceState => getInitialState(),
  actions: {
    fetchRaceContext (context: ActionContext<RaceState, RootState>, { raceId, raceTime, raceTimeMin, raceTimeMax }: RaceOptions): Promise<void> {
      context.commit('SET_RACE_ID', raceId)
      context.commit('SET_STATUS', Status.InProgress)
      context.commit('INCREMENT_VERSION')
      const currentVersion = context.state.version

      const url = context.rootState.config.BASE_FEEDS_URL + `Yachts/${raceId}/latest?raceTime=${raceTime}`

      return new Promise((resolve, reject) => {
        try {
          axios.get(url).then(response => {
            if (context.state.version === currentVersion) {
              context.commit('SET_FLEET_DATA', response.data)
              if (raceTime) {
                context.commit('SET_RACETIME', raceTime)
                context.commit('SET_RACE_DATETIME', raceTime)
              }
              if (raceTime && raceTimeMin && raceTimeMax) context.commit('SET_RACETIME_OPTIONS', { raceTimeMin, raceTimeMax })
              context.commit('SET_STATUS', Status.Success)
            }
          }).finally(resolve)
        } catch (e) {
          context.commit('SET_STATUS', Status.Error)
          reject(e)
        }
      })
    },
    fetchNewData (context: ActionContext<RaceState, RootState>, { raceTime }): Promise<void> {
      context.commit('SET_STATUS', Status.InProgress)
      context.commit('INCREMENT_VERSION')
      const currentVersion = context.state.version

      const url = context.rootState.config.BASE_FEEDS_URL + `Yachts/${context.state.raceId}/latest?raceTime=${raceTime}`

      return new Promise((resolve, reject) => {
        try {
          axios.get(url).then(response => {
            if (context.state.version === currentVersion) {
              context.commit('SET_FLEET_DATA', response.data)
              if (raceTime) {
                context.commit('SET_RACETIME', raceTime)
                context.commit('SET_RACE_DATETIME', raceTime)
              }
              context.commit('SET_STATUS', Status.Success)
            }
          }).finally(resolve)
        } catch (e) {
          context.commit('SET_STATUS', Status.Error)
          reject(e)
        }
      })
    },
    fetchAllTracks (context: ActionContext<RaceState, RootState>, { raceId, raceTime }): Promise<void> {
      context.commit('RESET_TRACKS')

      const requests = [] as Array<Promise<{ raceEntryId: number; track: Array<YachtTrackPoint>; }>>

      for (let i = 0; i < context.state.fleetData.length; i++) {
        const raceEntryId = context.state.fleetData[i].RaceEntryId
        const url = context.rootState.config.BASE_FEEDS_URL + `Yachts/Track/${raceId}/${raceEntryId}?raceTime=${raceTime}`

        requests.push(axios.get(url).then(response => ({ raceEntryId, track: response.data.slice(-1 * TRACK_LENGTH) })))
      }

      return new Promise((resolve, reject) => {
        try {
          Promise.all(requests).then(values => {
            context.commit('SET_TRACKS', values)
          })
            .finally(resolve)
        } catch (e) {
          reject(e)
        }
      })
    },
    fetchRhumbline (context: ActionContext<RaceState, RootState>, { raceId }): Promise<void> {
      const url = context.rootState.config.BASE_FEEDS_URL + `Course/Rhumbline/${raceId}`

      return new Promise((resolve, reject) => {
        try {
          axios.get(url).then(
            response => {
              context.commit('SET_RHUMBLINE_DATA', response.data)
            }
          ).finally(resolve)
        } catch (e) {
          reject(e)
        }
      })
    },
    reset (context: ActionContext<RaceState, RootState>): void {
      context.commit('RESET_STATE')
    }
  },
  mutations: {
    INCREMENT_VERSION (state: RaceState): void {
      state.version++
    },
    SET_RACE_ID (state: RaceState, payload: number): void {
      state.raceId = payload
    },
    SET_RACETIME (state: RaceState, payload: string): void {
      state.raceTime = payload
    },
    SET_RACE_DATETIME (state: RaceState, payload: string): void {
      state.raceDateTime = moment.tz(payload, 'YYYYMMDDHHmmss', 'Australia/Sydney').toDate()
    },
    SET_RACETIME_OPTIONS (state: RaceState, payload: { raceTimeMin: string; raceTimeMax: string; }): void {
      state.raceTimeMin = payload.raceTimeMin
      state.raceTimeMax = payload.raceTimeMax
      state.raceTimeOptions = generateValidRacetimeList(payload.raceTimeMin, payload.raceTimeMax)
    },
    SET_STATUS (state: RaceState, payload: Status): void {
      state.status = payload
    },
    SET_FLEET_DATA (state: RaceState, payload: Array<FleetDatapoint>): void {
      const fleetData = []
      const yachtFeatures = []

      for (let i = 0; i < payload.length; i++) {
        const competitor = payload[i]
        if (!competitor.ShowOnMap) continue
        if (!competitor.History) continue
        if (competitor.History.length === 0) continue
        competitor.status = getCompetitorStatus(competitor)
        competitor.render = {
          latitude: competitor.History[0].Latitude,
          longitude: competitor.History[0].Longitude,
          sog: competitor.History[0].SpeedOverGround,
          cog: competitor.History[0].CourseOverGround,
          timeUpdated: competitor.History[0].ReceiveTime
        }
        fleetData.push(competitor)

        yachtFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [competitor.render.longitude, competitor.render.latitude]
          },
          properties: {
            title: competitor.Name,
            sog: competitor.render.sog ? `${competitor.render.sog}kt` : '?? kt',
            icon: `icon-${competitor.status}`,
            relativeTime: relativeTime(competitor.render.timeUpdated)
          }
        })
      }

      state.fleetData = fleetData
      state.fleetSource.data.features = yachtFeatures
    },
    SET_RHUMBLINE_DATA (state: RaceState, payload: Array<RhumblinePoint>): void {
      state.rhumbline = payload
    },
    SET_TRACKS (state: RaceState, payload: Array<{ raceEntryId: number; track: Array<YachtTrackPoint>; }>): void {
      state.tracks = payload
    },
    RESET_TRACKS (state: RaceState): void {
      state.tracks = []
    },
    RESET_STATE (state: RaceState): void {
      state = getInitialState()
    }
  }
}

export default raceModule
